export interface PredefinedHabit {
  id: string;
  name: string;
  description: string;
  category: 'saude' | 'produtividade' | 'mindfulness' | 'relacionamentos' | 'aprendizado';
  difficulty: 'facil' | 'medio' | 'dificil';
  defaultDuration: number; // em minutos
  tips: string[];
  stackSuggestions: string[];

  // Campos para valores personalizados
  hasCustomValue?: boolean;
  customValueLabel?: string;
  customValueUnit?: string;
  customValueType?: 'number' | 'text';
}

export const predefinedHabits: PredefinedHabit[] = [
  // SAÚDE
  {
    id: 'beber-agua-acordar',
    name: 'Beber 1 copo de água ao acordar',
    description: 'Logo que acordar, antes de qualquer outra coisa, beba um copo de água para iniciar a hidratação.',
    category: 'saude',
    difficulty: 'facil',
    defaultDuration: 1,
    tips: [
      'Deixe um copo de água na cabeceira da cama na noite anterior.',
      'Beba devagar, saboreando a sensação de hidratação.',
      'Se quiser, adicione algumas rodelas de limão ou pepino para um sabor extra.'
    ],
    stackSuggestions: [
      'Após o alarme tocar',
      'Antes de sair da cama',
      'Depois de ir ao banheiro pela manhã'
    ]
  },
  {
    id: 'alongamento-matinal-cama',
    name: 'Alongamento de 5 minutos na cama',
    description: 'Assim que acordar, faça 5 minutos de alongamentos suaves ainda na cama para despertar o corpo.',
    category: 'saude',
    difficulty: 'facil',
    defaultDuration: 5,
    tips: [
      'Comece com alongamentos simples de braços e pernas.',
      'Foque na respiração, inspirando e expirando profundamente em cada movimento.',
      'Movimente pescoço, ombros e costas com cuidado.'
    ],
    stackSuggestions: [
      'Após beber água ao acordar',
      'Antes de levantar da cama',
      'Enquanto o café está sendo preparado'
    ]
  },
  {
    id: 'exercicio-2min-escovar',
    name: '2 minutos de exercício após escovar os dentes',
    description: 'Imediatamente após escovar os dentes, complete 2 minutos de exercícios leves.',
    category: 'saude',
    difficulty: 'facil',
    defaultDuration: 2,
    tips: [
      'Escolha um exercício simples como polichinelos, agachamentos leves ou flexões na parede.',
      'Mantenha uma rotina curta e consistente para criar o hábito.',
      'Use um timer para cronometrar os 2 minutos.'
    ],
    stackSuggestions: [
      'Depois de escovar os dentes',
      'Enquanto espera o café esquentar',
      'Antes de se vestir para o dia'
    ]
  },
  {
    id: 'caminhada-10min-almoco',
    name: 'Caminhada de 10 minutos após o almoço',
    description: 'Após terminar o almoço, faça uma caminhada revigorante de 10 minutos.',
    category: 'saude',
    difficulty: 'medio',
    defaultDuration: 10,
    tips: [
      'Escolha um caminho agradável perto do trabalho ou casa.',
      'Se possível, encontre um local com árvores ou natureza.',
      'Ouça um podcast ou música relaxante durante a caminhada.'
    ],
    stackSuggestions: [
      'Logo após terminar a refeição do almoço',
      'Antes de voltar ao trabalho',
      'Enquanto toma um ar fresco'
    ]
  },
  {
    id: 'exercicio-cardio-registrar',
    name: 'Fazer exercício cardiovascular e registrar tempo',
    description: 'Realize seu exercício cardiovascular e registre o tempo dedicado para acompanhar seu progresso.',
    category: 'saude',
    difficulty: 'medio',
    defaultDuration: 10,
    hasCustomValue: true,
    customValueLabel: 'Tempo',
    customValueUnit: 'min',
    customValueType: 'number',
    tips: [
      'Defina um horário fixo no seu dia para o exercício.',
      'Varie entre corrida, natação, ciclismo ou dança para não cair na rotina.',
      'Use um aplicativo ou relógio inteligente para monitorar o tempo e a intensidade.'
    ],
    stackSuggestions: [
      'No final da tarde, antes do jantar',
      'Após o trabalho, antes de relaxar',
      'Pela manhã, antes do café'
    ]
  },
  {
    id: 'registrar-humor-manha',
    name: 'Registrar o humor ao café da manhã',
    description: 'Durante o café da manhã, anote rapidamente uma palavra ou emoji que descreva seu humor/sentimento do momento. Isso ajuda na autoconsciência.',
    category: 'mindfulness',
    difficulty: 'facil',
    defaultDuration: 1, // Leva menos de 1 minuto para pensar e anotar
    hasCustomValue: true,
    customValueLabel: 'Humor/Sentimento',
    customValueUnit: 'ex: 😁, 😐, 😥', // Ou 'palavra'
    customValueType: 'text',
    tips: [
      'Use um caderno pequeno, um aplicativo de anotações ou até um diário de gratidão.',
      'Não precisa ser complexo, uma palavra ou emoji já basta.',
      'Com o tempo, você pode identificar padrões no seu humor.'
    ],
    stackSuggestions: [
      'Enquanto toma o primeiro gole de café',
      'Após terminar o café da manhã',
      'Antes de abrir o primeiro e-mail do trabalho'
    ]
  },
  // PRODUTIVIDADE
  {
    id: 'organizar-mesa-inicio',
    name: 'Organizar mesa antes de iniciar o trabalho',
    description: 'Antes de ligar o computador ou iniciar qualquer tarefa, dedique 3 minutos para organizar sua mesa de trabalho.',
    category: 'produtividade',
    difficulty: 'facil',
    defaultDuration: 3,
    tips: [
      'Remova papéis e objetos desnecessários.',
      'Limpe a superfície com um pano úmido.',
      'Arrume os itens essenciais em seus devidos lugares.'
    ],
    stackSuggestions: [
      'Antes de abrir o notebook',
      'Após tomar café da manhã e se sentar para trabalhar',
      'No final do dia, para o dia seguinte'
    ]
  },
  {
    id: 'revisar-agenda-manha',
    name: 'Revisar agenda pela manhã',
    description: 'Logo após o café da manhã, revise sua agenda para o dia e priorize as tarefas mais importantes.',
    category: 'produtividade',
    difficulty: 'facil',
    defaultDuration: 3,
    tips: [
      'Identifique as 3 tarefas que trarão maior impacto no seu dia.',
      'Bloqueie horários específicos para cada tarefa na sua agenda.',
      'Use um planejador físico ou digital para visualizar seus compromissos.'
    ],
    stackSuggestions: [
      'Após o café da manhã',
      'Antes de verificar e-mails',
      'Enquanto toma a primeira xícara de café'
    ]
  },
  {
    id: 'pomodoro-primeira-tarefa',
    name: 'Uma sessão Pomodoro na primeira tarefa',
    description: 'Inicie o dia com uma sessão Pomodoro de 25 minutos, focando na sua tarefa mais importante.',
    category: 'produtividade',
    difficulty: 'medio',
    defaultDuration: 25,
    tips: [
      'Elimine todas as distrações (notificações, redes sociais).',
      'Use um timer dedicado ou um aplicativo Pomodoro.',
      'Recompense-se com um breve descanso após cada sessão.'
    ],
    stackSuggestions: [
      'Após revisar a agenda',
      'Depois de responder os e-mails urgentes',
      'Assim que sentar na sua cadeira de trabalho'
    ]
  },

  // MINDFULNESS
  {
    id: 'respiracao-profunda-estresse',
    name: 'Respiração profunda após estresse',
    description: 'Após uma situação estressante ou antes de um evento importante, pratique 2 minutos de respiração profunda para acalmar-se.',
    category: 'mindfulness',
    difficulty: 'facil',
    defaultDuration: 2,
    tips: [
      'Sente-se em um local tranquilo e feche os olhos, se possível.',
      'Concentre-se em inspirar pelo nariz e expirar pela boca lentamente.',
      'Sinta o abdômen expandir e contrair a cada respiração.'
    ],
    stackSuggestions: [
      'Após uma reunião tensa',
      'Antes de uma apresentação importante',
      'Quando sentir a mente agitada'
    ]
  },
  {
    id: 'meditacao-5min-dormir',
    name: 'Meditação de 5 minutos antes de dormir',
    description: 'Antes de deitar, dedique 5 minutos à meditação mindfulness para acalmar a mente e preparar-se para o sono.',
    category: 'mindfulness',
    difficulty: 'medio',
    defaultDuration: 5,
    tips: [
      'Use um aplicativo de meditação guiada ou simplesmente observe sua respiração.',
      'Deite-se confortavelmente ou sente-se na beirada da cama.',
      'Não se apegue aos pensamentos, apenas os observe e deixe-os ir.'
    ],
    stackSuggestions: [
      'Depois de escovar os dentes à noite',
      'Após ler um livro',
      'Assim que deitar na cama'
    ]
  },
  {
    id: 'gratidao-jantar',
    name: 'Anotar 3 gratidões após o jantar',
    description: 'Após o jantar, antes de iniciar suas atividades noturnas, anote 3 coisas pelas quais você é grato naquele dia.',
    category: 'mindfulness',
    difficulty: 'facil',
    defaultDuration: 3,
    tips: [
      'Mantenha um diário de gratidão próximo à mesa de jantar.',
      'Pense em momentos, pessoas ou coisas simples que te trouxeram alegria.',
      'Seja específico para tornar a gratidão mais poderosa.'
    ],
    stackSuggestions: [
      'Depois de recolher a mesa do jantar',
      'Antes de ligar a TV',
      'Enquanto faz a digestão do jantar'
    ]
  },

  // RELACIONAMENTOS
  {
    id: 'mensagem-carinho-cafe',
    name: 'Enviar mensagem de carinho durante o café',
    description: 'Enquanto toma seu café da manhã, envie uma mensagem carinhosa para alguém importante em sua vida.',
    category: 'relacionamentos',
    difficulty: 'facil',
    defaultDuration: 2,
    tips: [
      'Pode ser um "bom dia", um elogio, ou algo que te fez lembrar da pessoa.',
      'Alterne entre amigos, familiares, colegas ou parceiros.',
      'Evite mensagens genéricas; personalize-as.'
    ],
    stackSuggestions: [
      'Durante o café da manhã',
      'Após verificar as primeiras notícias do dia',
      'Antes de iniciar o trabalho'
    ]
  },
  {
    id: 'telefonema-familia-jantar',
    name: 'Ligar para familiar após o jantar',
    description: 'Após o jantar, faça uma ligação rápida de 5 minutos para um familiar para saber como ele está.',
    category: 'relacionamentos',
    difficulty: 'medio',
    defaultDuration: 5,
    tips: [
      'Tenha em mente alguns tópicos para conversar.',
      'Ouça atentamente o que a pessoa tem a dizer.',
      'Evite distrações durante a chamada.'
    ],
    stackSuggestions: [
      'Depois de terminar o jantar',
      'Antes de assistir TV ou mexer nas redes sociais',
      'Enquanto relaxa no sofá'
    ]
  },
  {
    id: 'elogio-colegas',
    name: 'Fazer um elogio a um colega de trabalho',
    description: 'Durante o expediente, encontre uma oportunidade para fazer um elogio genuíno a um colega de trabalho.',
    category: 'relacionamentos',
    difficulty: 'facil',
    defaultDuration: 1,
    tips: [
      'Seja específico sobre o que você elogiou (uma ideia, um trabalho bem feito, uma atitude).',
      'Pode ser pessoalmente ou por mensagem interna.',
      'Foque em algo que você realmente admira na pessoa.'
    ],
    stackSuggestions: [
      'Após uma reunião de equipe',
      'Depois de ver um bom desempenho de alguém',
      'Ao final do dia de trabalho'
    ]
  },

  // APRENDIZADO
  {
    id: 'leitura-10min-dormir',
    name: 'Ler por 10 minutos antes de dormir',
    description: 'Antes de apagar a luz para dormir, leia um livro ou artigo de interesse por 10 minutos.',
    category: 'aprendizado',
    difficulty: 'medio',
    defaultDuration: 10,
    tips: [
      'Mantenha o livro ou e-reader perto da cama.',
      'Escolha um gênero que te relaxe e prenda sua atenção.',
      'Evite telas luminosas (celular, tablet) antes de dormir.'
    ],
    stackSuggestions: [
      'Após escovar os dentes e se preparar para a cama',
      'Depois de meditar, se for o caso',
      'Assim que deitar na cama'
    ]
  },
  {
    id: 'podcast-educativo-transporte',
    name: 'Ouvir podcast educativo no transporte',
    description: 'Enquanto estiver no transporte público ou dirigindo, ouça 15 minutos de um podcast educativo.',
    category: 'aprendizado',
    difficulty: 'facil',
    defaultDuration: 15,
    tips: [
      'Baixe os episódios com antecedência para não depender da internet.',
      'Escolha temas que contribuam para seus objetivos pessoais ou profissionais.',
      'Aproveite o tempo ocioso para aprender algo novo.'
    ],
    stackSuggestions: [
      'Ao entrar no carro/ônibus/metrô',
      'Durante o trajeto para o trabalho ou faculdade',
      'Enquanto espera em alguma fila'
    ]
  },
  {
    id: 'nova-palavra-cafe',
    name: 'Aprender palavra nova durante o café da manhã',
    description: 'Enquanto toma o café da manhã, aprenda uma palavra nova (em português ou outro idioma) e tente usá-la durante o dia.',
    category: 'aprendizado',
    difficulty: 'facil',
    defaultDuration: 3,
    tips: [
      'Use um aplicativo de vocabulário ou um dicionário online.',
      'Anote a palavra, seu significado e uma frase de exemplo.',
      'Crie um lembrete para usar a palavra em uma conversa ou escrita.'
    ],
    stackSuggestions: [
      'Enquanto toma café da manhã',
      'Após ler as notícias da manhã',
      'Antes de sair de casa'
    ]
  },
  {
    id: 'curso-online-jantar',
    name: 'Assistir aula online após o jantar',
    description: 'Após o jantar, dedique 20 minutos para assistir a uma aula curta de um curso online.',
    category: 'aprendizado',
    difficulty: 'medio',
    defaultDuration: 20,
    tips: [
      'Escolha um curso que seja do seu interesse e que possa aplicar no dia a dia.',
      'Crie um ambiente de estudo tranquilo e livre de distrações.',
      'Faça anotações dos pontos-chave da aula.'
    ],
    stackSuggestions: [
      'Depois de lavar a louça do jantar',
      'Antes de ligar a TV para o entretenimento',
      'No mesmo horário todas as noites'
    ]
  }
];

export const getHabitsByCategory = (category: string) => {
  return predefinedHabits.filter(habit => habit.category === category);
};

export const getHabitById = (id: string) => {
  return predefinedHabits.find(habit => habit.id === id);
};

export const categories = [
  { id: 'saude', name: 'Saúde', icon: '💪', color: 'green' },
  { id: 'produtividade', name: 'Produtividade', icon: '⚡', color: 'blue' },
  { id: 'mindfulness', name: 'Mindfulness', icon: '🧘', color: 'purple' },
  { id: 'relacionamentos', name: 'Relacionamentos', icon: '❤️', color: 'pink' },
  { id: 'aprendizado', name: 'Aprendizado', icon: '📚', color: 'yellow' }
];